import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useUserProfile } from '../../contexts/UserProfileContext';

interface AdminGuardProps {
  children: React.ReactNode;
}

const AdminGuard: React.FC<AdminGuardProps> = ({ children }) => {
  const { apiCall, isLoading: isAuthLoading, isSignedIn, user } = useAuth();
  const { userProfile } = useUserProfile();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(true);

  useEffect(() => {
    let isMounted = true;

    // Fast path: use userProfile from context if already loaded (no extra API call)
    if (userProfile && userProfile.id === user?.userId) {
      if (isMounted) {
        setIsAdmin(userProfile.is_admin === true);
        setIsCheckingAdmin(false);
      }
      return;
    }

    // Not signed in or no user
    if (!isSignedIn || !user?.userId) {
      if (isMounted) {
        setIsAdmin(false);
        setIsCheckingAdmin(false);
      }
      return;
    }

    const checkAdminStatus = async () => {
      try {
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        const response = await apiCall(`${apiBaseUrl}/api/v1/profiles/${user.userId}`);
        if (isMounted) {
          if (response.ok) {
            const profile = await response.json();
            setIsAdmin(profile.is_admin === true);
          } else {
            setIsAdmin(false);
          }
        }
      } catch (error) {
        console.error('Failed to check admin status:', error);
        if (isMounted) setIsAdmin(false);
      } finally {
        if (isMounted) setIsCheckingAdmin(false);
      }
    };

    if (!isAuthLoading) {
      checkAdminStatus();
    }

    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, user?.userId, isAuthLoading, userProfile?.id, userProfile?.is_admin]);

  // Show loading while checking admin
  if (isCheckingAdmin || isAuthLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show access denied if not admin
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-pink-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Access Restricted</h1>
          <p className="text-gray-600 mb-6">
            This feature is currently only available for admin users.
          </p>
          <a
            href="/"
            className="inline-block px-6 py-3 bg-gradient-to-r from-pink-500 to-orange-400 text-white font-medium rounded-lg hover:from-pink-600 hover:to-orange-500 transition-all"
          >
            Go Home
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AdminGuard;

